import type { FilterOptionCollectionItemHookProps } from '@components/FilterOptionMultiLevelCollections';
import { useFilterSettings, useFilterState, useFilterTreeState } from '@providers/FilterProvider';
import { slugify } from '@utils';
import { useCallback, useMemo, useState } from 'react';

type FilterOptionCollectionItemHookState = {
  isExpanded: boolean;
};

export const useFilterOptionCollectionItem = ({
  collectionId,
  collectionHandle,
  tag,
  subTags,
  hasChildListFilterOptionItem,
}: FilterOptionCollectionItemHookProps) => {
  const {
    currentCollectionSelected,
    setCurrentCollectionSelected,
    historyCollection,
    setHistoryCollection,
  } = useFilterState();
  const { showFilterOptionCount } = useFilterSettings();
  const { refineByHorizontal, setRefineByHorizontal } = useFilterTreeState();

  const isOptionItemSelected = useMemo(() => {
    const isCollectionSelected =
      collectionId.toString() === currentCollectionSelected?.collectionId?.toString();

    if (!isCollectionSelected) {
      return false;
    }

    if (tag) {
      const currentPageTags = currentCollectionSelected.tag;

      let hasSubTagSelected = false;

      if (subTags) {
        hasSubTagSelected = subTags.some(({ tag }) => currentPageTags === tag);
      }

      // toLowerCase(): cover case back button get label from url ->changed Lower
      return hasSubTagSelected || currentPageTags?.toLowerCase() === tag?.toLowerCase();
    }

    return isCollectionSelected;
  }, [collectionId, tag, subTags, currentCollectionSelected]);

  const collectionUrlPath = useMemo(() => {
    /**
     * NOTE: update later
     */

    const routesRoot = window?.Shopify?.routes?.root || '/';

    return `${routesRoot}collections/${collectionHandle}${tag ? `/${slugify(tag)}` : ''}`;
  }, [collectionHandle, tag]);

  const [state, setState] = useState<FilterOptionCollectionItemHookState>({
    isExpanded: isOptionItemSelected,
  });

  const toggleChildListExpandedState = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      isExpanded: !prevState.isExpanded,
    }));
  }, []);

  const isChildListExpanded = useMemo(() => {
    return hasChildListFilterOptionItem && state.isExpanded;
  }, [hasChildListFilterOptionItem, state.isExpanded]);

  const handleSelectOptionItem = (e: Event) => {
    e.preventDefault();

    if (!isOptionItemSelected) {
      // save all collection selected
      setHistoryCollection({
        ...historyCollection,
        [slugify(tag || collectionHandle || (Math.random() + 1).toString(36))]: {
          collectionId: collectionId,
          handle: collectionHandle,
          tag: tag,
        },
      });

      // current collection selected
      setCurrentCollectionSelected({
        collectionId,
        handle: collectionHandle,
        tag,
      });

      if (refineByHorizontal && Object.keys(refineByHorizontal).length > 0) {
        setRefineByHorizontal({});
      }
    }
  };

  return {
    isOptionItemSelected,
    collectionUrlPath,
    isChildListExpanded,
    toggleChildListExpandedState,
    handleSelectOptionItem,
    showFilterOptionCount,
  };
};
